import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import {
  Archive,
  Building2,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Package,
  Settings,
  Shield,
  Sun,
  Trash2,
  TrendingDown,
  TrendingUp,
  User,
  Users,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '../utils/helpers';
import ChangePasswordDialog from './ChangePasswordDialog';

const roleLabels = {
  administrator: 'Administrator',
  admin_finance: 'Admin Finance',
  staff_finance: 'Staff Finance',
};

const getNavItems = (isAdministrator) => {
  const items = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/pemasukan', label: 'Pemasukan', icon: TrendingUp },
    { path: '/pengeluaran', label: 'Pengeluaran', icon: TrendingDown },
    { path: '/inventori', label: 'Inventori', icon: Package },
    { path: '/cabang', label: 'Cabang', icon: Building2 },
  ];

  if (isAdministrator) {
    items.push(
      { path: '/users', label: 'User Management', icon: Users },
      { path: '/security', label: 'Security Center', icon: Shield },
      { path: '/site-manager', label: 'Site Manager', icon: Settings },
      { path: '/database', label: 'Database Cleaning', icon: Trash2 },
      { path: '/backups', label: 'Backup Center', icon: Archive },
    );
  }

  return items;
};

export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { isAdministrator } = useAuth();
  const { siteSettings } = useApp();
  const navItems = getNavItems(isAdministrator);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border/50 bg-background/95 backdrop-blur transition-transform duration-300 ease-in-out md:static',
          isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-border/50 px-6">
          <h1 className="text-xl font-black tracking-tight text-foreground">
            {siteSettings?.nama_perusahaan || 'Swiss Ledger'}
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onClose}
            data-testid="close-sidebar-btn"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-4">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  'flex items-center gap-3 rounded-sm px-4 py-3 text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'border border-border/60 bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground',
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border/50 p-4">
          <p className="text-center text-xs text-muted-foreground">
            (c) 2026 {siteSettings?.nama_perusahaan || 'Swiss Ledger'}
          </p>
        </div>
      </aside>
    </>
  );
};

export const Header = ({ onMenuClick }) => {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && theme === 'dark';

  return (
    <>
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border/50 bg-background/90 px-6 backdrop-blur-md">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onMenuClick}
            data-testid="open-sidebar-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h2 className="hidden text-lg font-bold text-foreground sm:block">
            Sistem Keuangan & Inventori
          </h2>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-2 sm:flex">
            <Sun className="h-4 w-4 text-amber-500" />
            <Switch
              checked={isDark}
              onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              aria-label="Toggle dark mode"
              data-testid="theme-switch"
            />
            <Moon className="h-4 w-4 text-sky-500" />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                  <User className="h-4 w-4" />
                </div>
                <div className="hidden text-left sm:block">
                  <p className="text-sm font-medium">{user?.nama || user?.username}</p>
                  <p className="text-xs text-muted-foreground">{roleLabels[user?.role] || user?.role}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.nama || user?.username}</p>
                  <p className="text-xs text-muted-foreground">{user?.username}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
                className="sm:hidden"
                data-testid="theme-switch-mobile"
              >
                {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                {isDark ? 'Mode Terang' : 'Mode Gelap'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowChangePassword(true)} data-testid="change-password-menu">
                <User className="mr-2 h-4 w-4" />
                Ubah Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-rose-600" data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
    </>
  );
};

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { siteSettings } = useApp();

  useEffect(() => {
    const name = (siteSettings?.nama_perusahaan || '').trim();
    document.title = name ? `${name} FinTech` : 'Swiss Ledger';
  }, [siteSettings?.nama_perusahaan]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex min-h-screen">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex min-w-0 flex-1 flex-col">
          <Header onMenuClick={() => setSidebarOpen(true)} />
          <main className="flex-1 animate-fade-in p-4 md:p-6 lg:p-8">{children}</main>
        </div>
      </div>
    </div>
  );
};
